import * as React from 'react';
import * as cssDefs from 'consumer-page-widgets/basicCarouselWidgetCssDefs';
import {
  BasicCarouselWidget as BasicCarouselWidgetType,
  Modifiers,
} from '@tock/shared/types/widget';
import { Grid } from 'ui';
import { Link } from 'omakase';
import { ScrollableList } from 'consumer-page-widgets/ScrollableList';
import { WidgetCard } from 'consumer-page-widgets/WidgetCard';
import { WidgetContainer } from 'consumer-page-widgets/WidgetContainer';
import { WidgetListItemContent } from 'types';
import { consumer } from '@tock/coretypes';
import {
  getTockContent,
  getTockMedium,
  mapBusinessDataToItems,
  mapContentToItems,
  mapExperienceDataToItems,
} from 'utils/widgetUtils';
import { useInView } from 'react-intersection-observer';
import WidgetLayoutContainer from 'consumer-page-widgets/WidgetUtils/WidgetLayoutContainer';

export interface BasicCarouselWidgetProps {
  baseWidgetClassName?: string;
  className?: string;
  id?: number;
  isTitle: boolean;
  lazyLoad: boolean;
  pathname: string;
  widget:
    | { mode: 'legacy'; carousel: BasicCarouselWidgetType }
    | { mode: 'widgetBuilder'; carousel: consumer.IGridWidget };
  listIndex?: number;
}

export const BasicCarouselWidget: React.FC<BasicCarouselWidgetProps> = ({
  widget,
  className,
  isTitle,
  pathname,
  lazyLoad = false,
  listIndex,
}) => {
  const { ref, inView } = useInView({
    rootMargin: '200px 0px',
    initialInView: !lazyLoad,
    triggerOnce: true,
  });

  const getDetails = (): { title: string; subtitle: string; detailsPage: string } => {
    switch (widget.mode) {
      case 'legacy': {
        const { widgetInfo } = widget.carousel;
        const { title, subtitle, detailsPage } = widgetInfo as consumer.WidgetInfo;
        return { title, subtitle, detailsPage };
      }
      case 'widgetBuilder': {
        const grid = widget.carousel;
        return {
          title: grid.title || '',
          subtitle: grid.subtitle || '',
          detailsPage: grid.linkHref || '',
        };
      }

      default:
        return { title: '', subtitle: '', detailsPage: '' };
    }
  };

  const { title, subtitle, detailsPage } = getDetails();

  const getItems = (): WidgetListItemContent[] => {
    switch (widget.mode) {
      case 'legacy': {
        return mapContentToItems(widget.carousel.content as consumer.WidgetContent, 'IMGIX');
      }
      case 'widgetBuilder': {
        const grid = widget.carousel;
        const dataType = (grid as consumer.GridWidget).data;
        switch (dataType) {
          case 'businessData':
            return mapBusinessDataToItems(grid.businessData?.business || [], 'IMGIX');
          case 'experienceData':
            return mapExperienceDataToItems(grid.experienceData?.experience || []);

          default:
            return [];
        }
      }

      default:
        return [];
    }
  };

  const widgetDataItems = getItems();

  if (widgetDataItems.length < 1) {
    return null;
  }

  const getModifiers = () => {
    switch (widget.mode) {
      case 'legacy':
        return (widget.carousel.widgetInfo?.widgetModifier as consumer.WidgetModifier[]) ?? [];

      default:
        return [];
    }
  };

  // these modifiers are used for breakpoint behavior and subtitle types in the legacy system. We have not built these features in the widget builder system so we can ignore them for now
  const widgetModifiers = getModifiers();

  const titleType = widgetModifiers.find((modifier) => modifier.key === Modifiers.titleType);

  const titleTypeValue = titleType?.value;

  const slides = widgetDataItems.map((item: WidgetListItemContent, index: number) => {
    return (
      <Grid
        key={item.key}
        item
        xs={12}
        sm={6}
        md={3}
        component="li"
        data-testid={
          item.type === 'EXPERIENCE' ? `result-experience-${index}` : `result-business-${index}`
        }
        css={item.type === 'EXPERIENCE' ? cssDefs.gridItemExperience : undefined}
      >
        {lazyLoad && !inView ? (
          // approximate card
          <div css={cssDefs.card} style={{ paddingTop: '93%' }} />
        ) : (
          <WidgetCard
            pathname={pathname}
            item={item}
            modifiers={widgetModifiers}
            css={cssDefs.card}
            widgetListIndex={listIndex}
            widgetName={title || undefined}
            index={index}
          />
        )}
      </Grid>
    );
  });

  const href = `${detailsPage}?tock_source=tock&tock_medium=${getTockMedium(
    pathname
  )}&tock_content=${getTockContent('carousel', title)}`;

  return (
    <WidgetLayoutContainer ref={ref} className={className}>
      <WidgetContainer
        title={title}
        isTitle={isTitle}
        isSubSection={titleTypeValue ? titleTypeValue === 'subsection' : false}
        subtitle={subtitle}
        css={cssDefs.gridContent}
        href={href}
        contains="Widget Carousel"
      >
        <ScrollableList>
          {slides.concat([
            <Grid item xs={12} sm={6} md={3} component="li" key={`view-all-${detailsPage}`}>
              <div css={cssDefs.emptyCard}>
                <Link size="jumbo" href={href}>
                  View all
                </Link>
              </div>
            </Grid>,
          ])}
        </ScrollableList>
      </WidgetContainer>
    </WidgetLayoutContainer>
  );
};
